/**
 * @description
 * Invoice table component for displaying invoice list.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { Link } from 'react-router';
import { Eye, Download, Edit } from 'lucide-react';
import type { InvoiceTableProps } from './types';

export function InvoiceTable({
  invoices,
  selectedInvoices,
  onSelectInvoice,
  onSelectAll,
}: InvoiceTableProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'void':
        return 'bg-red-100 text-red-800';
      case 'uncollectible':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    return type === 'usage' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b">
            <tr>
              <th className="p-4 text-left">
                <input
                  type="checkbox"
                  checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                  onChange={onSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="p-4 text-left text-sm font-medium">Invoice</th>
              <th className="p-4 text-left text-sm font-medium">Client</th>
              <th className="p-4 text-left text-sm font-medium">Amount</th>
              <th className="p-4 text-left text-sm font-medium">Status</th>
              <th className="p-4 text-left text-sm font-medium">Type</th>
              <th className="p-4 text-left text-sm font-medium">Created</th>
              <th className="p-4 text-left text-sm font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="border-b hover:bg-muted/50">
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.includes(invoice.id)}
                    onChange={() => onSelectInvoice(invoice.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="p-4">
                  <div className="font-medium">{invoice.id}</div>
                  <div className="text-sm text-muted-foreground">
                    {invoice.dueDate && `Due: ${new Date(invoice.dueDate).toLocaleDateString()}`}
                  </div>
                </td>
                <td className="p-4">
                  <Link
                    to={`/clients/${invoice.clientId}`}
                    className="font-medium hover:text-primary"
                  >
                    {invoice.clientName}
                  </Link>
                </td>
                <td className="p-4">
                  <div className="font-medium">{formatCurrency(invoice.amountDue)}</div>
                  {invoice.amountPaid > 0 && (
                    <div className="text-sm text-green-600">
                      Paid: {formatCurrency(invoice.amountPaid)}
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(invoice.invoiceType)}`}>
                    {invoice.invoiceType}
                  </span>
                </td>
                <td className="p-4 text-sm text-muted-foreground">
                  {new Date(invoice.createdAt).toLocaleDateString()}
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    {invoice.hostedInvoiceUrl && (
                      <a
                        href={invoice.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                        title="View Invoice"
                      >
                        <Eye className="h-4 w-4" />
                      </a>
                    )}
                    {invoice.invoicePdf && (
                      <a
                        href={invoice.invoicePdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                    )}
                    <button
                      onClick={() => {/* Edit invoice */}}
                      className="text-primary hover:underline"
                      title="Edit Invoice"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invoices.length === 0 && (
        <div className="p-8 text-center text-muted-foreground">
          <p>No invoices found matching your criteria.</p>
        </div>
      )}
    </div>
  );
}
